import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import customFetch from "../util/config/axiosConfig";
import { logout, passwordChanged } from "../features/user/UserSlice";
import { AppDispatch } from "../store";

const INPUT = "w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-main";

/**
 * Shown instead of the app when a team member signs in with the temporary password their admin gave them:
 * they choose their own password first.
 */
export default function ChangePasswordGate({ name }: { name?: string }) {
  const dispatch = useDispatch<AppDispatch>();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) return setError("The new password must have at least 8 characters.");
    if (next !== again) return setError("The two new passwords don't match.");
    setBusy(true);
    setError(null);
    try {
      await customFetch.post("/api/team/me/password", { currentPassword: current, newPassword: next });
      dispatch(passwordChanged());
      toast.success("Password changed");
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Couldn't change the password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-gray-50 p-4">
      <form onSubmit={save} className="w-full max-w-md rounded-3xl bg-white p-8 shadow-sm grid gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Choose your password</h1>
        <p className="text-sm text-gray-500 mb-2">
          {name ? `Welcome, ${name}. ` : ""}Your account was created by your team admin with a temporary password. Choose
          your own to continue.
        </p>
        <input className={INPUT} type="password" placeholder="Temporary password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        <input className={INPUT} type="password" placeholder="New password (8+ characters)" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        <input className={INPUT} type="password" placeholder="New password again" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
        {error && <p className="text-sm text-red-500" role="alert">{error}</p>}
        <button
          type="submit"
          disabled={busy || !current || !next}
          className="mt-2 px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save and continue"}
        </button>
        <button type="button" className="text-sm text-gray-500 hover:underline" onClick={() => dispatch(logout("Signed out"))}>
          Sign out
        </button>
      </form>
    </div>
  );
}
